import {
  AGENT_MEMORY_SCOPES,
  type AgentMemoryScope
} from "../../../../api/domains/agent-memory/schemas/core";
import { type NexusApiActionDef } from "./action-def.types";
import { type NexusApiFieldDef } from "./field.types";
import { bool, num, obj, str } from "./output-format.builders";

// ── Agent memory ──────────────────────────────────────────────────────────────
//
// 🔴 THE SCOPE VOCABULARY IS IMPORTED, NEVER RETYPED. `AGENT_MEMORY_SCOPES` is the
// only declaration of the set, pinned in the database by
// `AgentMemory_scope_matches_arm`. A hand-written array here would be a looser copy
// of a CHECK constraint on the surface a builder configures, which is a bypass
// rather than a duplicate — and `MEMORY_SCOPE_LABELS` is a `Record` over the same
// type, so a fourth arm is a compile error here until somebody labels it.
//
// ⚠️ `scope` HERE IS NOT `Scope` IN THE CUE SENSE (one permission a Cue API
// key carries) NOR `Org scope` / `Role scope` IN THE ROLES SENSE. Three vocabularies,
// one word; `20260827190000_add_agent_memory`'s header records the collision.

const MEMORY_SCOPE_LABELS: Record<AgentMemoryScope, string> = {
  chat: "Conversation (this chat only)",
  customer: "Customer (survives the chat closing)",
  agent: "Agent (shared across every customer)"
};

const memoryScopeField: NexusApiFieldDef = {
  type: "string",
  description:
    "Which thing the memory belongs to. `customer` is the one that outlives a conversation.",
  constraints: {
    allowedValues: [...AGENT_MEMORY_SCOPES],
    allowedOptions: AGENT_MEMORY_SCOPES.map((value) => ({
      label: MEMORY_SCOPE_LABELS[value],
      value
    }))
  }
};

const memorySubjectIdField: NexusApiFieldDef = {
  type: "string",
  description:
    "UUID of the subject the memory belongs to — a chat id, a customer id or an agent id, matching `scope`. " +
    "An `agentInputTrigger` with `includeMetadata` publishes `_metadata.conversationId` (the chat) and " +
    "`_metadata.agentId` on the trigger's own output, so those two scopes can be wired from the graph directly."
};

export const memoryActions: NexusApiActionDef[] = [
  {
    action: "memory.get",
    label: "Read Agent Memory",
    description:
      "Read every stored memory entry for one subject. Returns an empty `values` object when nothing has been stored yet — reading is never an error.",
    inputSchema: {
      scope: memoryScopeField,
      subjectId: memorySubjectIdField
    },
    // 🔴 THE ENVELOPE IS DECLARABLE AND THE FIELD SHAPE IS NOT, SO `values` IS
    // DECLARED **OPEN ON PURPOSE** AND EVERY SIBLING IS A TYPED LEAF.
    //
    // This is the first action whose real output shape is not a compile-time
    // constant. A catalogue entry is one module-level object shared by every
    // organisation; the keys inside `values` are whatever that organisation's
    // ACTIVE `MemorySchema` row declares — per-org, per-scope, versioned, and
    // append-only, so a value read here can predate the code reading it. There is
    // no honest way to publish those keys from here, and publishing a GUESS would
    // be worse than publishing nothing: `buildValidVariablePaths` would then refuse
    // a reference to a key the organisation really declared.
    //
    // So the split is deliberate and it is not a cop-out — it is what makes the
    // strict path check useful on this node rather than vacuous:
    //
    //  - the ROOT carries `properties`, so it is NOT open, so
    //    `{{memoryNode.nonsense}}` has a declared ancestor that cannot be descended
    //    and the strict walk REFUSES it;
    //  - `values` carries no `properties`, so it IS open, so
    //    `{{memoryNode.values.customer_tier}}` is granted descent — which is
    //    correct, because only the organisation knows whether that key exists.
    //
    // The alternative — a bare `obj("Agent memory")` like `inbox.getMetadata` —
    // leaves the WHOLE node open, so `{{node.anything.at.all}}` is granted
    // descent and a typo is never refused. That is the sin #4915 was landed to
    // close. Declaring the envelope is the strongest true statement available,
    // and it is worth more than a false complete one.
    outputFormat: obj("Result", {
      scope: str("The scope that was read"),
      subjectId: str("The subject that was read"),
      exists: bool(
        "Whether this subject has a memory record at all. `false` means nothing has ever been written — distinct from a record whose entries are all gone."
      ),
      entryCount: num("How many entries `values` carries"),
      storedMemoryBytes: num(
        "The subject's stored charge in bytes, as the write door counts it (`key + value + 4` per entry). `0` when `exists` is false."
      ),
      values: obj(
        "The stored entries, as a flat key/value object. Its keys are whatever this organisation's active memory schema declares for this scope, so they cannot be published here — reference one as `{{node.values.your_key}}`."
      )
    })
  },
  {
    action: "memory.set",
    label: "Write Agent Memory",
    description:
      "Store one memory entry for a subject. The key must already be declared by the memory PAD this node names, on this organisation's active schema for that pad, and the subject's total is capped at 8000 bytes.",
    inputSchema: {
      scope: memoryScopeField,
      subjectId: memorySubjectIdField,
      pad: {
        type: "string",
        description:
          "The memory PAD this write belongs to, e.g. `orange_commande_nouvel_abonnement`. A pad is one named field group inside a scope: it decides which keys this node may write and what an agent is told they mean. It does NOT partition the customer's record — every entry lands in one shared document that every agent reads. Publishing refuses an unknown pad, and a key the named pad does not declare, by name."
      },
      key: {
        type: "string",
        description:
          "The declared memory key to write, e.g. `preferred_language`. A key the named pad does not declare is refused."
      },
      value: {
        type: "string",
        description:
          "The value to store, as text. Charged as `key + value + 4` bytes against the subject's 8000-byte budget."
      }
    },
    outputFormat: obj("Result", {
      scope: str("The scope that was written"),
      subjectId: str("The subject that was written"),
      key: str("The key that was written"),
      storedMemoryBytes: num("The subject's total charge AFTER this write"),
      appliedDelta: num("What the subject's total actually moved by")
    })
  }
];
